import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaCopy, FaFolder, FaVideo, FaFilePdf, FaFileAlt, FaYoutube } from 'react-icons/fa';
import { toast } from 'react-toastify';
import BatchMaterialService from '../../../../services/batchMaterialService';

const AdoptMaterialsModal = ({ show, onHide, currentBatchId, onAdopt }) => {
  const [availableBatches, setAvailableBatches] = useState([]);
  const [selectedSourceBatch, setSelectedSourceBatch] = useState('');
  const [sourceMaterials, setSourceMaterials] = useState([]);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  useEffect(() => {
    if (show) {
      loadAvailableBatches();
    }
  }, [show]);

  const loadAvailableBatches = async () => {
    try {
      setLoading(true);
      const response = await BatchMaterialService.getAvailableBatches(currentBatchId);
      setAvailableBatches(response.data || []);
    } catch (error) {
      toast.error('Failed to load available batches');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadSourceBatchMaterials = async (batchId) => {
    if (!batchId) {
      setSourceMaterials([]);
      return;
    }

    setLoadingMaterials(true);
    try {
      const response = await BatchMaterialService.getAllMaterialsForBatch(batchId);
      setSourceMaterials(response.data?.tree || []);
    } catch (error) {
      toast.error('Failed to load source batch materials');
      console.error(error);
    } finally {
      setLoadingMaterials(false);
    }
  };

  const handleSourceBatchChange = async (e) => {
    const sourceId = e.target.value;
    setSelectedSourceBatch(sourceId);
    setSelectedMaterialIds([]);
    if (sourceId) {
      await loadSourceBatchMaterials(sourceId);
    } else {
      setSourceMaterials([]);
    }
  };

  const toggleMaterialSelection = (materialId, isChecked) => {
    if (isChecked) {
      setSelectedMaterialIds((prev) => [...prev, materialId]);
    } else {
      setSelectedMaterialIds((prev) => prev.filter((id) => id !== materialId));
    }
  };

  const toggleSelectAll = () => {
    const allIds = getAllMaterialIdsFromTree(sourceMaterials);
    if (selectedMaterialIds.length === allIds.length && allIds.length > 0) {
      setSelectedMaterialIds([]);
    } else {
      setSelectedMaterialIds(allIds);
    }
  };

  const getAllMaterialIdsFromTree = (items) => {
    let ids = [];
    items.forEach((item) => {
      ids.push(item.id);
      if (item.children && item.children.length > 0) {
        ids = [...ids, ...getAllMaterialIdsFromTree(item.children)];
      }
    });
    return ids;
  };

  const handleAdopt = async () => {
    if (!selectedSourceBatch) {
      toast.error('Please select a source batch');
      return;
    }

    if (selectedMaterialIds.length === 0) {
      toast.error('Please select at least one material to adopt');
      return;
    }

    try {
      setLoading(true);
      const response = await BatchMaterialService.adoptMaterials(
        selectedSourceBatch,
        currentBatchId,
        selectedMaterialIds
      );
      toast.success(response.message || `${response.data?.copied_count || selectedMaterialIds.length} materials adopted successfully`);
      onAdopt();
      onHide();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to adopt materials');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedSourceBatch('');
    setSourceMaterials([]);
    setSelectedMaterialIds([]);
  };

  const getMaterialIcon = (type) => {
    switch (type) {
      case 'folder':
        return <FaFolder className="text-warning me-2" />;
      case 'video':
        return <FaVideo className="text-primary me-2" />;
      case 'pdf':
        return <FaFilePdf className="text-danger me-2" />;
      case 'link':
        return <FaYoutube className="text-danger me-2" />;
      default:
        return <FaFileAlt className="text-secondary me-2" />;
    }
  };

  const renderMaterialTree = (items, level = 0) => {
    return items.map((item) => {
      const isChecked = selectedMaterialIds.includes(item.id);

      return (
        <div
          key={item.id}
          style={{ marginLeft: `${level * 25}px` }}
          className="mb-2"
        >
          <div className="d-flex align-items-center">
            <Form.Check
              type="checkbox"
              id={`adopt-checkbox-${item.id}`}
              checked={isChecked}
              onChange={(e) => toggleMaterialSelection(item.id, e.target.checked)}
              className="me-2"
            />
            {getMaterialIcon(item.type)}
            <span>{item.name}</span>
            <Badge bg="secondary" className="ms-2">
              {item.type}
            </Badge>
          </div>
          {item.children && item.children.length > 0 && renderMaterialTree(item.children, level + 1)}
        </div>
      );
    });
  };

  return (
    <Modal show={show} onHide={() => {
      resetForm();
      onHide();
    }} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCopy className="me-2 text-info" />
          Adopt Materials from Another Batch
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <strong>How it works:</strong> Select a source batch, choose the materials you want to copy,
          and they will be added to the current batch. Files are copied to the new batch.
        </Alert>

        <Form.Group className="mb-4">
          <Form.Label>Select Source Batch</Form.Label>
          <Form.Select
            value={selectedSourceBatch}
            onChange={handleSourceBatchChange}
            disabled={loading}
          >
            <option value="">-- Select a batch --</option>
            {availableBatches.map(batch => (
              <option key={batch.id} value={batch.id}>
                {batch.name} ({batch.course_name})
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Materials will be copied from this batch to the current batch
          </Form.Text>
        </Form.Group>

        {selectedSourceBatch && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Materials to Adopt:</h6>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={toggleSelectAll}
                disabled={loadingMaterials}
              >
                {selectedMaterialIds.length === getAllMaterialIdsFromTree(sourceMaterials).length &&
                getAllMaterialIdsFromTree(sourceMaterials).length > 0
                  ? 'Deselect All'
                  : 'Select All'}
              </Button>
            </div>

            {loadingMaterials ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
                <p className="mt-2">Loading materials...</p>
              </div>
            ) : sourceMaterials.length === 0 ? (
              <Alert variant="warning">No materials found in the selected batch.</Alert>
            ) : (
              <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {renderMaterialTree(sourceMaterials)}
              </div>
            )}

            <div className="mt-3">
              <Badge bg="primary">
                Selected: {selectedMaterialIds.length} material(s)
              </Badge>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => {
          resetForm();
          onHide();
        }}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleAdopt}
          disabled={!selectedSourceBatch || selectedMaterialIds.length === 0 || loading}
        >
          <FaCopy className="me-2" /> Adopt Selected Materials
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdoptMaterialsModal;